import React, { useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  Plus,
  Trash2,
  BookOpen,
  Table,
} from 'lucide-react';
import { deleteSemester } from '@/services/filieresService';
import type { SemesterWithModules, WriteGuard } from '@/types/filiere.types';
import ModuleCard from './ModuleCard';
import CreateModuleModal from './CreateModuleModal';
import BulkModuleEditor from './BulkModuleEditor';
import DeleteImpactModal from './filieres/DeleteImpactModal';

interface SemesterCardProps {
  semester: SemesterWithModules;
  onUpdate: () => void;
  onError: (msg: string) => void;
  onSuccess: (msg: string) => void;
  canManage?: boolean;
  /** Wraps every write so the page can show a campus-scope warning first */
  guardWrite?: WriteGuard;
}

const runDirectly: WriteGuard = (action) => action();

const SemesterCard: React.FC<SemesterCardProps> = ({
  semester,
  onUpdate,
  onError,
  onSuccess,
  canManage = true,
  guardWrite = runDirectly,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCreateModule, setShowCreateModule] = useState(false);
  const [showBulkEditor, setShowBulkEditor] = useState(false);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteSemester(semester.id);
      onSuccess(`Semestre ${semester.name} supprimé`);
      onUpdate();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Erreur lors de la suppression');
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-primary-50 to-blue-50 cursor-pointer hover:from-primary-100 hover:to-blue-100 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <div className="min-w-[2.5rem] h-10 px-2 rounded-lg bg-white shadow-sm flex items-center justify-center">
            <span className="font-bold text-primary-600 text-sm whitespace-nowrap">S{semester.number}</span>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2 flex-wrap">
              <span>Semestre {semester.number}</span>
              {semester.optionFiliere && (
                <span className="text-xs font-medium px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">
                  {semester.optionFiliere.optionName || semester.optionFiliere.filiere}
                </span>
              )}
            </h3>
            <p className="text-sm text-gray-500">
              {semester.modules.length} module{semester.modules.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {canManage && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                guardWrite(() => setShowDeleteConfirm(true));
              }}
              className="p-2 text-red-500 hover:bg-red-100 rounded-lg transition-colors"
              title="Supprimer le semestre"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </div>
      </div>

      {/* Content */}
      {isExpanded && (
        <div className="p-4 bg-white">
          {/* Add Module Buttons */}
          {canManage && (
            <div className="flex justify-end gap-2 mb-4">
              <button
                onClick={() => guardWrite(() => setShowBulkEditor(true))}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-purple-600 hover:bg-purple-50 rounded-lg transition-colors border border-purple-200"
              >
                <Table className="w-4 h-4" />
                Ajouter plusieurs modules
              </button>
              <button
                onClick={() => guardWrite(() => setShowCreateModule(true))}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
              >
                <Plus className="w-4 h-4" />
                Ajouter un Module
              </button>
            </div>
          )}

          {/* Modules */}
          {semester.modules.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <BookOpen className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">Aucun module dans ce semestre</p>
              {canManage && (
                <button
                  onClick={() => guardWrite(() => setShowCreateModule(true))}
                  className="mt-2 text-primary-600 hover:text-primary-700 text-sm"
                >
                  + Ajouter un module
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {semester.modules.map((module) => (
                <ModuleCard
                  key={module.id}
                  module={module}
                  canManage={canManage}
                  guardWrite={guardWrite}
                  onUpdate={onUpdate}
                  onError={onError}
                  onSuccess={onSuccess}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation (with impact) */}
      <DeleteImpactModal
        isOpen={showDeleteConfirm}
        targets={[{ type: 'semester', id: semester.id }]}
        title={`Supprimer ${semester.name} ?`}
        message="Le semestre, ses modules et leurs éléments seront supprimés, ainsi que les données rattachées ci-dessous."
        isLoading={isDeleting}
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      />

      {/* Create Module Modal */}
      {showCreateModule && (
        <CreateModuleModal
          semesterId={semester.id}
          onClose={() => setShowCreateModule(false)}
          onSuccess={() => {
            setShowCreateModule(false);
            onSuccess('Module créé avec succès');
            onUpdate();
          }}
          onError={onError}
        />
      )}

      {/* Bulk Module Editor */}
      {showBulkEditor && (
        <BulkModuleEditor
          semesterId={semester.id}
          semesterNumber={semester.number}
          onClose={() => setShowBulkEditor(false)}
          onSuccess={() => {
            setShowBulkEditor(false);
            onSuccess('Modules créés avec succès');
            onUpdate();
          }}
          onError={onError}
        />
      )}
    </div>
  );
};

export default SemesterCard;
